import React from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { compose } from 'redux';
import memoize from 'memoize-one';
import cn from 'classnames';
import theme from '../../styles/theme';
import { cardType } from '../../propTypes';
import Card, { twoColumnBreakpoint, mobilePortraitWidth } from './Card';
import {
  areSliceCardsMinimal,
  getSliceCards,
  getSliceMobileSideScroller,
  getSliceCardAspect,
  isBreakpointSet,
  isCurrentBreakpointAtLeast,
  getSliceColumns,
  getSliceMobileColumns,
  isSmallViewport,
  getSliceCardTitleLocation,
  getSliceCardTitleColor,
  getSliceCardOpacity,
} from '../../selectors';
import MobileSideScroller from '../Scrollers/MobileSideScroller';
import aspectsRatiosByColumn from './aspects';

const Cards = ({
  classes,
  groups,
  isMinimal,
  mobileSideScroller,
  formats,
  isOverlay,
  isDark,
  opacity,
  mobileColumns,
}) => (
  <div className={cn(classes.wrapper, !isMinimal && classes.notMinimal)}>
    {Object.keys(groups).map(format => {
      const Container = format === 'portrait' ? MobileSideScroller : 'div';
      const containerProps = format === 'portrait' ? {
        slideClassName: classes.card,
      } : undefined;
      const className = cn(
        classes[`${format}Cards`],
        mobileSideScroller && classes[`${format}CardsWithMobileSideScroller`],
      );
      const isStackedOnMobileInOneColumn = !mobileSideScroller && mobileColumns === 1;
      return (
        <Container key={format} className={className} {...containerProps}>
          {groups[format].map((card, i) => (
            <Card
              key={i} // eslint-disable-line react/no-array-index-key
              className={cn(
                classes.card,
                isMinimal && classes.minimalCard,
                classes[`${card.format}Card`],
                mobileSideScroller && classes[`${card.format}CardWithMobileSideScroller`],
                !mobileSideScroller && classes[`${card.format}CardWithoutMobileSideScroller`],
              )}
              classNames={{ image: cn(classes[`${card.format}Image`]) }}
              minimal={isMinimal}
              formats={formats}
              isOverlay={isOverlay}
              isDark={isDark}
              opacity={opacity}
              isStackedOnMobileInOneColumn={isStackedOnMobileInOneColumn}
              {...card}
            />
          ))}
        </Container>
      );
    })}
  </div>
);

const opacityOptions = ['No opacity', 'Light opacity', 'Dark opacity'];

Cards.propTypes = {
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  groups: PropTypes.objectOf(PropTypes.arrayOf(cardType)).isRequired,
  isMinimal: PropTypes.bool.isRequired,
  mobileSideScroller: PropTypes.bool,
  // eslint-disable-next-line react/no-unused-prop-types -- used in mapStateToProps
  sliceId: PropTypes.number.isRequired,
  formats: PropTypes.shape({
    portrait: PropTypes.number,
    landscape: PropTypes.number,
  }),
  // eslint-disable-next-line react/no-unused-prop-types
  columns: PropTypes.number, // used in the styles
  mobileColumns: PropTypes.number,
  isOverlay: PropTypes.bool,
  isDark: PropTypes.shape({
    desktop: PropTypes.bool,
    mobile: PropTypes.bool,
  }),
  opacity: PropTypes.shape({
    desktop: PropTypes.oneOf(opacityOptions),
    mobile: PropTypes.oneOf(opacityOptions),
  }),
};

Cards.defaultProps = {
  mobileSideScroller: true,
  formats: undefined,
  columns: 3,
  mobileColumns: 1,
  isOverlay: false,
  isDark: { desktop: false, mobile: false },
  opacity: { desktop: 'No opacity', mobile: 'No opacity' },
};

const mapStateToProps = () => {
  const groupCards = memoize(cards => cards.reduce((grouped, card) => ({
    ...grouped,
    [card.format]: [
      ...(grouped[card.format] || []),
      card,
    ],
  }), {}));

  return (state, { sliceId }) => {
    const cards = getSliceCards(state, sliceId);
    const isMobile = isBreakpointSet(state) && !isCurrentBreakpointAtLeast(state, 'md');
    const mobileSideScroller = getSliceMobileSideScroller(state, sliceId);

    const groups = isMobile && mobileSideScroller ? groupCards(cards) : { all: cards };

    const columns = getSliceColumns(state, sliceId);
    const mobileColumns = getSliceMobileColumns(state, sliceId);
    const aspectRatio = getSliceCardAspect(state, sliceId);
    const currentViewportAspectRatio = isSmallViewport(state)
      ? aspectRatio.mobile : aspectRatio.desktop;
    const aspects = aspectsRatiosByColumn[columns || '3'];
    const formats = aspects[currentViewportAspectRatio] || aspects.default;

    const isOverlay = getSliceCardTitleLocation(state, sliceId);
    const isDark = getSliceCardTitleColor(state, sliceId);
    const isMinimal = !isOverlay ? areSliceCardsMinimal(state, sliceId) : isOverlay;
    const opacity = getSliceCardOpacity(state, sliceId);
    return {
      groups,
      isMinimal,
      mobileSideScroller,
      formats,
      columns: columns ? parseInt(columns, 10) : 3,
      mobileColumns: mobileColumns ? parseInt(mobileColumns, 10) : 1,
      isOverlay,
      isDark,
      opacity,
    };
  };
};

const negativeGutters = {
  marginLeft: -theme.padding.gutter / 2,
  marginRight: -theme.padding.gutter / 2,
};
export default compose(
  connect(mapStateToProps),

  injectSheet({
    wrapper: {
      marginTop: 16,
    },
    notMinimal: {
      // negative bottom margin to soak up the bottom margin of the last row of cards
      [theme.breakpoints.up('md')]: {
        marginBottom: -(theme.spacing.vertical.md - theme.padding.gutter),
      },
    },
    allCards: {
      // note: this applies on desktop, but also on mobile prior to hydration and swiper
      // initialisation
      display: 'flex',
      flexWrap: 'wrap',
      ...negativeGutters,
    },
    allCardsWithMobileSideScroller: {
      [theme.breakpoints.only('sm')]: {
        flexWrap: 'nowrap',
        marginRight: -theme.padding.horizontal, // extend to the right-hand edge of the viewport
        overflow: 'hidden', // stop the page width blowing out
      },
    },
    card: {
      margin: [0, theme.padding.gutter / 2, theme.spacing.vertical.xs],
      flex: '0 0 auto',
    },
    minimalCard: {
      marginBottom: theme.padding.gutter,
    },
    portraitCard: {
      maxWidth: ({ columns }) => (columns === 2 ? 'auto' : 416),
      [theme.breakpoints.up('md')]: {
        width: ({ columns }) => `calc((100% - ${theme.padding.gutter * columns}px) / ${columns})`,
      },
    },
    portraitCardWithoutMobileSideScroller: {
      [theme.breakpoints.only('sm')]: {
        width: `calc((100% - ${theme.padding.gutter * 2}px) / 2)`,
      },
      [theme.breakpoints.between(0, twoColumnBreakpoint)]: {
        width: ({ mobileColumns }) => (mobileColumns === 1 ? `calc(100% - ${theme.padding.gutter}px)` : null),
      },
    },
    portraitCardWithMobileSideScroller: {
      [theme.breakpoints.only('sm')]: {
        width: mobilePortraitWidth,
      },
    },
    landscapeCard: {
      width: `calc(100% - ${theme.padding.gutter}px)`,
      maxWidth: ({ columns }) => (columns === 2 ? 'auto' : 848),
      [theme.breakpoints.up('md')]: {
        width: ({ columns }) => `calc(2 * (100% - ${theme.padding.gutter * columns}px) / ${columns} + ${theme.padding.gutter}px)`,
      },
    },
    landscapeCardWithMobileSideScroller: {
      [theme.breakpoints.only('sm')]: {
        // pre-hydration, make landscape images the same height as portrait ones on mobile so that
        // they don’t stretch out the height of the fake css-only carousel
        width: formats => (mobilePortraitWidth / formats.portrait) * formats.landscape,
      },
    },
    landscapeImage: {
      [theme.breakpoints.between('md', theme.breakpoints.width('xl') + theme.padding.horizontal * 2)]: {
        height: ({ columns }) => `calc(4 * (100vw - ${theme.padding.gutter * columns + theme.padding.horizontal * 2}px) / 9)`,
      },
    },
    landscapeCards: {
      ...negativeGutters,
      '& $landscapeCard': {
        // mobile-specific, post-hydration
        width: 'auto',
      },
    },
  }),
)(Cards);
